import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowUpRight } from "@phosphor-icons/react";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import { Link as RouterLink } from "react-router-dom";
import { addYears } from "date-fns";
import LabeledTextArea from "../../Widgets/LabeledTextArea";
import LabeledDatePicker from "../../Widgets/LabeledDatePicker";
import LabeledSelect from "../../Widgets/LabeledSelect";
import useGetCustomerPhysicians from "hooks/api/client/useGetCustomerPhysicians";
import useGetAllDocumentRequests from "hooks/api/client/useGetAllDocumentRequests";

interface Physician {
  id: number | null;
  name: string | null;
  clinic_id: number | null;
}

interface DocumentRequest {
  id: number;
  provider_id: number | null;
  clinic_id: number | null;
  signature_url: string | null;
  document_id: string | null;
  created: string;
}

interface InterventionFormValues {
  startDate: Date | null;
  endDate: Date | null;
  servicesProvided: string;
  ongoing: string;
  perceivedEffectiveness: string;
  providerName: string;
}

export interface InterventionModalSaveValues {
  startDate: Date | null;
  endDate: Date | null;
  servicesProvided: string;
  ongoing: string;
  perceivedEffectiveness: string;
  providerName: string;
}

const INITIAL_FORM: InterventionFormValues = {
  startDate: null,
  endDate: null,
  servicesProvided: "",
  ongoing: "",
  perceivedEffectiveness: "",
  providerName: "",
};

const ONGOING_OPTIONS = [
  { value: "YES", label: "Yes" },
  { value: "NO", label: "No" },
  { value: "UNKNOWN", label: "Unknown" },
];

interface InterventionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: InterventionModalSaveValues) => void;
  isSaving?: boolean;
  initialValues?: Partial<InterventionFormValues>;
  clientId: string | number;
}

export const InterventionModal: React.FC<InterventionModalProps> = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  initialValues,
  clientId,
}) => {
  const { physicians } = useGetCustomerPhysicians(clientId);
  const { documentRequests } = useGetAllDocumentRequests(clientId);
  // The hook types id as non-null, but clinic-only care-team rows return id: null.
  const providerList = (physicians ?? []) as unknown as Physician[];

  const getRoiLabel = (physician: Physician): string | null => {
    const signed = ((documentRequests as DocumentRequest[]) ?? []).filter(
      (req) =>
        (req.provider_id === physician.id || req.clinic_id === physician.clinic_id) &&
        (req.signature_url || req.document_id)
    );
    if (signed.length === 0) return null;
    const latest = [...signed].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    )[0];
    const expired = addYears(new Date(latest.created), 1) < new Date();
    return expired ? "ROI expired" : "ROI on file";
  };

  // Clinic-only care-team rows have no physician id, so key them by clinic id instead.
  const providerKey = (p: Physician) => (p.id != null ? String(p.id) : `clinic-${p.clinic_id}`);

  const [formValues, setFormValues] = useState<InterventionFormValues>({
    ...INITIAL_FORM,
    ...initialValues,
  });
  const [isCustomProvider, setIsCustomProvider] = useState(false);
  const [selectedProviderKey, setSelectedProviderKey] = useState<string>("");

  useEffect(() => {
    if (open) {
      setFormValues({ ...INITIAL_FORM, ...initialValues });
      const matched = providerList.find((p) => p.name === initialValues?.providerName);
      setSelectedProviderKey(matched ? providerKey(matched) : "");
      setIsCustomProvider(!matched && !!initialValues?.providerName);
    }
  }, [open, initialValues, physicians]);

  const handleChange =
    (field: keyof InterventionFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleDateChange = (field: keyof InterventionFormValues) => (value: Date | null) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    onSave({
      startDate: formValues.startDate,
      endDate: formValues.endDate,
      servicesProvided: formValues.servicesProvided,
      ongoing: formValues.ongoing,
      perceivedEffectiveness: formValues.perceivedEffectiveness,
      providerName: formValues.providerName,
    });
  };

  const isEditMode = Boolean(initialValues && Object.keys(initialValues).length > 0);
  const selectedProvider = providerList.find((p) => providerKey(p) === selectedProviderKey);

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Edit Intervention" : "Add New Intervention"}
      maxWidth="xs"
      drawerContentSx={{ maxHeight: "calc(80vh - 160px)" }}
    >
      <Box sx={{ p: 0 }}>
        <Grid container spacing={7} sx={{ p: "24px 0px" }}>
          <LabeledDatePicker
            label="Start Date"
            value={formValues.startDate}
            onChange={handleDateChange("startDate")}
            slotProps={{ popper: { disablePortal: false } }}
          />
          <LabeledDatePicker
            label="End Date"
            value={formValues.endDate}
            onChange={handleDateChange("endDate")}
            slotProps={{ popper: { disablePortal: false } }}
          />
          <LabeledTextArea
            label="Services Provided"
            value={formValues.servicesProvided}
            onChange={handleChange("servicesProvided")}
            multiline={false}
          />
          <LabeledSelect
            label="Services Ongoing"
            value={formValues.ongoing}
            onChange={handleChange("ongoing")}
            options={ONGOING_OPTIONS}
          />
        </Grid>

        <Stack gap={1} sx={{ mt: 7 }}>
          {/* @ts-expect-error - inputLabel is a custom variant defined in theme but TS doesn't recognize it */}
          <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
            Intervention Provider
          </Typography>
          <Select
            value={selectedProviderKey}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "__add_name__") {
                setIsCustomProvider(true);
                setSelectedProviderKey("");
                setFormValues((prev) => ({ ...prev, providerName: "" }));
              } else {
                const provider = providerList.find((p) => providerKey(p) === val);
                setIsCustomProvider(false);
                setSelectedProviderKey(val);
                setFormValues((prev) => ({ ...prev, providerName: provider?.name ?? "" }));
              }
            }}
            displayEmpty
            renderValue={(selected) => {
              if (isCustomProvider) return <span>{formValues.providerName || ""}</span>;
              if (!selected) return <em>None</em>;
              return <span>{selectedProvider?.name ?? ""}</span>;
            }}
            sx={{ borderRadius: "10px" }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {providerList.map((member) => {
              const roiLabel = getRoiLabel(member);
              return (
                <MenuItem
                  key={providerKey(member)}
                  value={providerKey(member)}
                  sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
                >
                  <span>{member.name}</span>
                  {roiLabel && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: roiLabel === "ROI expired" ? "error.main" : "text.secondary",
                        flexShrink: 0,
                      }}
                    >
                      {roiLabel}
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
            <MenuItem value="__add_name__">+ Add Name Only</MenuItem>
          </Select>

          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Release of Information Consent
            </Typography>
            <Typography variant="body2">
              If you&apos;re actively collaborating with this provider, add them to the Care Team
              first to send an ROI to the caregiver.
            </Typography>
          </Alert>
          <Link
            component={RouterLink}
            to={`/clients/${clientId}/care-team`}
            underline="hover"
            sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
          >
            <Typography variant="body2">Go to Care Team</Typography>
            <ArrowUpRight size={16} />
          </Link>

          {isCustomProvider && (
            <Stack gap={1}>
              {/* @ts-expect-error - inputLabel is a custom variant defined in theme but TS doesn't recognize it */}
              <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
                Intervention Provider Name
              </Typography>
              <TextField
                value={formValues.providerName}
                onChange={handleChange("providerName")}
                autoFocus
              />
            </Stack>
          )}
        </Stack>

        <Grid container spacing={7} sx={{ mt: 0 }}>
          <LabeledTextArea
            label="Perceived Effectiveness"
            value={formValues.perceivedEffectiveness}
            onChange={handleChange("perceivedEffectiveness")}
            multiline={false}
          />
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mt: "16px" }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton variant="contained" size="small" loading={isSaving} onClick={handleSave}>
            {isEditMode ? "Save Changes" : "Add Intervention"}
          </LoadingButton>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default InterventionModal;
